import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, User, UserPlus, Briefcase, DollarSign, Package, Loader2, Clock, Trash2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { SearchResult } from '../lib/types';

const RECENT_SEARCHES_KEY = 'flusia_recent_searches';
const MAX_RECENT_SEARCHES = 10;

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  query: string;
  onQueryChange: (query: string) => void;
  results: SearchResult[];
  loading: boolean;
}

const TYPE_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  client: {
    label: 'Cliente',
    icon: <User className="h-3.5 w-3.5" />,
    color: 'text-[#E91E8C]',
    bg: 'bg-[rgba(233,30,140,0.1)]',
  },
  lead: {
    label: 'Lead',
    icon: <UserPlus className="h-3.5 w-3.5" />,
    color: 'text-[#8B5CF6]',
    bg: 'bg-[rgba(139,92,246,0.1)]',
  },
  project: {
    label: 'Progetto',
    icon: <Briefcase className="h-3.5 w-3.5" />,
    color: 'text-[#3B82F6]',
    bg: 'bg-[rgba(59,130,246,0.1)]',
  },
  deal: {
    label: 'Offerta',
    icon: <DollarSign className="h-3.5 w-3.5" />,
    color: 'text-[#10b981]',
    bg: 'bg-[rgba(16,185,129,0.1)]',
  },
  product: {
    label: 'Prodotto',
    icon: <Package className="h-3.5 w-3.5" />,
    color: 'text-[#F59E0B]',
    bg: 'bg-[rgba(245,158,11,0.1)]',
  },
};

function TypeBadge({ type }: { type: string }) {
  const cfg = TYPE_CONFIG[type];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color} flex-shrink-0`}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

export function SearchOverlay({
  isOpen,
  onClose,
  query,
  onQueryChange,
  results,
  loading
}: SearchOverlayProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [recentSearches, setRecentSearches] = useState<SearchResult[]>([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) setRecentSearches(JSON.parse(stored));
    } catch (e) { /* noop */ }
  }, []);

  const saveRecentSearch = useCallback((result: SearchResult) => {
    setRecentSearches(prev => {
      const filtered = prev.filter(r => !(r.type === result.type && r.id === result.id));
      const updated = [result, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      try { localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated)); } catch (e) { /* noop */ }
      return updated;
    });
  }, []);

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    try { localStorage.removeItem(RECENT_SEARCHES_KEY); } catch (e) { /* noop */ }
  }, []);

  const handleResultClick = (result: SearchResult) => {
    saveRecentSearch(result);
    onClose();
    if (result.type === 'lead') {
      window.dispatchEvent(new CustomEvent('open-lead-modal', { detail: { entityId: result.id } }));
    } else if (result.type === 'client') {
      window.dispatchEvent(new CustomEvent('open-client-modal', { detail: { entityId: result.id } }));
    } else {
      navigate(result.link);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setTimeout(() => setMounted(false), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const activeList = query.trim().length >= 2 ? results : recentSearches;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, activeList.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && activeList[selectedIndex]) {
        e.preventDefault();
        handleResultClick(activeList[selectedIndex]);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, selectedIndex, results, recentSearches, query, onClose]);

  useEffect(() => {
    setSelectedIndex(0);
    setSelectedCategory('all');
  }, [results, query]);

  if (!mounted) return null;

  const categoryCounts = results.reduce((acc, r) => {
    acc[r.type] = (acc[r.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const filteredResults = selectedCategory === 'all' ? results : results.filter(r => r.type === selectedCategory);

  const groupedResults = filteredResults.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  const overlayContent = (
    <div
      className={`fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh] px-4 transition-opacity duration-200 bg-black/30 dark:bg-black/60 backdrop-blur-sm ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-2xl transition-all duration-200 transform ${
          isOpen ? 'translate-y-0 opacity-100' : '-translate-y-3 opacity-0'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Card unificata: input + chips + risultati in un unico blocco rounded */}
        <div className="bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.12)] dark:border-[#3F4447] rounded-2xl shadow-2xl overflow-hidden">

          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3.5">
            {loading
              ? <Loader2 className="h-4 w-4 text-[#787774] animate-spin flex-shrink-0" />
              : <Search className="h-4 w-4 text-[#787774] flex-shrink-0" />
            }
            <input
              ref={inputRef}
              type="text"
              className="flex-1 bg-transparent text-sm text-[#37352F] dark:text-white placeholder-[#9B9A97] dark:placeholder-[#787774] focus:outline-none"
              placeholder="Cerca lead, clienti, offerte, progetti…"
              value={query}
              onChange={e => onQueryChange(e.target.value)}
            />
            {query && (
              <button
                onClick={() => onQueryChange('')}
                className="flex-shrink-0 p-1 rounded-md hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Category chips */}
          {query.trim().length >= 2 && results.length > 0 && (
            <div className="border-t border-[rgba(55,53,47,0.06)] dark:border-[#3F4447]/60 px-4 py-2 overflow-x-auto">
              <div className="flex gap-1.5 min-w-max">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                    selectedCategory === 'all'
                      ? 'bg-[#37352F] dark:bg-white text-white dark:text-[#37352F]'
                      : 'text-[#787774] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] hover:text-[#37352F] dark:hover:text-white'
                  }`}
                >
                  Tutte
                  <span className={`text-[10px] px-1 py-0.5 rounded ${selectedCategory === 'all' ? 'bg-white/20 dark:bg-black/20' : 'bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.08]'}`}>
                    {results.length}
                  </span>
                </button>
                {Object.entries(categoryCounts).map(([type, count]) => {
                  const cfg = TYPE_CONFIG[type];
                  const isActive = selectedCategory === type;
                  return (
                    <button
                      key={type}
                      onClick={() => setSelectedCategory(type)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                        isActive
                          ? `${cfg?.bg ?? ''} ${cfg?.color ?? ''}`
                          : 'text-[#787774] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] hover:text-[#37352F] dark:hover:text-white'
                      }`}
                    >
                      {cfg?.label ?? type}
                      <span className={`text-[10px] px-1 py-0.5 rounded ${isActive ? 'bg-black/10' : 'bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.08]'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Results list */}
          {query.trim().length >= 2 && (
            <div className="border-t border-[rgba(55,53,47,0.06)] dark:border-[#3F4447]/60 max-h-[55vh] overflow-y-auto">
              {loading ? (
                <div className="px-4 py-10 flex flex-col items-center gap-2 text-[#787774]">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <p className="text-xs">Ricerca in corso…</p>
                </div>
              ) : results.length > 0 ? (
                <div className="py-1.5">
                  {Object.entries(groupedResults).map(([type, typeResults], groupIndex) => {
                    const cfg = TYPE_CONFIG[type];
                    return (
                      <div key={type}>
                        <div className={`flex items-center gap-2 px-4 py-1.5 ${groupIndex > 0 ? 'mt-1 border-t border-[rgba(55,53,47,0.06)] dark:border-[#3F4447]/60 pt-2.5' : ''}`}>
                          <span className={`${cfg?.color ?? 'text-[#787774]'}`}>{cfg?.icon}</span>
                          <span className="text-[10px] font-semibold text-[#9B9A97] dark:text-[#787774] uppercase tracking-wider">
                            {cfg?.label ?? type}
                          </span>
                          <span className="text-[10px] text-[#9B9A97] dark:text-[#787774]">— {typeResults.length}</span>
                        </div>
                        {typeResults.map(result => {
                          const globalIndex = results.indexOf(result);
                          const isSelected = globalIndex === selectedIndex;
                          return (
                            <div
                              key={`${result.type}-${result.id}`}
                              onClick={() => handleResultClick(result)}
                              onMouseEnter={() => setSelectedIndex(globalIndex)}
                              className={`flex items-center gap-3 mx-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                isSelected
                                  ? 'bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.05]'
                                  : 'hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03]'
                              }`}
                            >
                              <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${cfg?.bg ?? ''} ${cfg?.color ?? ''}`}>
                                {cfg?.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#37352F] dark:text-white truncate">{result.title}</p>
                                {result.subtitle && (
                                  <p className="text-xs text-[#787774] dark:text-[#9B9A97] truncate">{result.subtitle}</p>
                                )}
                              </div>
                              {isSelected && <ArrowRight className="h-3.5 w-3.5 text-[#787774] flex-shrink-0" />}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-4 py-10 flex flex-col items-center gap-2 text-[#787774]">
                  <Search className="h-7 w-7 text-[#C7C5C2] dark:text-[#4A4A4A]" />
                  <p className="text-sm font-medium text-[#37352F] dark:text-white">Nessun risultato</p>
                  <p className="text-xs">Prova con parole chiave diverse</p>
                </div>
              )}
            </div>
          )}

          {/* Recent searches */}
          {query.trim().length === 0 && recentSearches.length > 0 && (
            <div className="border-t border-[rgba(55,53,47,0.06)] dark:border-[#3F4447]/60 max-h-[55vh] overflow-y-auto">
              <div className="py-1.5">
                <div className="flex items-center justify-between px-4 py-1.5">
                  <div className="flex items-center gap-1.5 text-[#9B9A97] dark:text-[#787774]">
                    <Clock className="h-3 w-3" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider">Recenti</span>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); clearRecentSearches(); }}
                    className="flex items-center gap-1 text-[10px] text-[#9B9A97] hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                    Cancella
                  </button>
                </div>
                {recentSearches.map((result, index) => {
                  const cfg = TYPE_CONFIG[result.type];
                  const isSelected = index === selectedIndex;
                  return (
                    <div
                      key={`recent-${result.type}-${result.id}`}
                      onClick={() => handleResultClick(result)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={`flex items-center gap-3 mx-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.05]'
                          : 'hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03]'
                      }`}
                    >
                      <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${cfg?.bg ?? 'bg-[rgba(55,53,47,0.06)]'} ${cfg?.color ?? 'text-[#787774]'}`}>
                        {cfg?.icon ?? <Clock className="h-3.5 w-3.5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#37352F] dark:text-white truncate">{result.title}</p>
                        {result.subtitle && (
                          <p className="text-xs text-[#787774] dark:text-[#9B9A97] truncate">{result.subtitle}</p>
                        )}
                      </div>
                      <TypeBadge type={result.type} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Typing hint */}
          {query.trim().length < 2 && query.length > 0 && (
            <div className="border-t border-[rgba(55,53,47,0.06)] dark:border-[#3F4447]/60 px-4 py-5 text-center text-[#9B9A97] text-xs">
              Digita almeno 2 caratteri per iniziare la ricerca
            </div>
          )}

        </div>{/* fine card unificata */}

        {/* Keyboard hints */}
        <div className="mt-2.5 flex justify-center">
          <div className="inline-flex items-center gap-3 bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] px-3 py-1.5 rounded-xl shadow-md text-[10px] text-[#9B9A97] dark:text-[#787774]">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.06] rounded text-[9px] font-medium text-[#787774] dark:text-[#9B9A97]">↑↓</kbd>
              Naviga
            </span>
            <span className="w-px h-3 bg-[rgba(55,53,47,0.1)] dark:bg-white/10" />
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.06] rounded text-[9px] font-medium text-[#787774] dark:text-[#9B9A97]">Enter</kbd>
              Apri
            </span>
            <span className="w-px h-3 bg-[rgba(55,53,47,0.1)] dark:bg-white/10" />
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.06] rounded text-[9px] font-medium text-[#787774] dark:text-[#9B9A97]">Esc</kbd>
              Chiudi
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlayContent, document.body);
}
